import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrayParam, StringParam, useQueryParams } from 'use-query-params'

import classes from '../SearchPage.module.scss'

import {
  useCountryOptions,
  useInfiniteQuery,
  useQueryCountryCodeValidation
} from '@percent/workplace-giving/common/hooks'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'
import { Page } from '@percent/workplace-giving/common/components/Layout/Page'
import { SearchPageHeader } from '@percent/workplace-giving/common/components/SearchPageHeader/SearchPageHeader'
import { mapInfiniteData } from '@percent/workplace-giving/utils/mapInfiniteData'
import { useInfiniteScroll } from '@percent/workplace-giving/common/hooks/useInfiniteScroll/useInfinteScroll'
import { Flex, IllustratedMessage } from '@percent/lemonade'
import { searchOpportunities } from '@percent/workplace-giving/api/search/searchOpportunities/searchOpportunities'
import { SwitchToGlobalResults } from '@percent/workplace-giving/common/components/SwitchToGlobalResults/SwitchToGlobalResults'
import { OpportunityCard } from '@percent/workplace-giving/common/components/OpportunityCard/OpportunityCard'
import { OpportunityCardSkeleton } from '@percent/workplace-giving/common/components/OpportunityCard/OpportunityCardSkeleton'
import { VolunteeringFiltersModal } from '@percent/workplace-giving/common/components/VolunteeringFiltersModal/VolunteeringFiltersModal'
import { FiltersButton } from '@percent/workplace-giving/common/components/FiltersButton/FiltersButton'
import { volunteeringAllowedOrgCountriesAlpha3 } from '@percent/workplace-giving/common/data/sanctionedCountries'
import { usePermission } from '@percent/workplace-giving/common/hooks/usePermission/usePermission'
import { scope } from '@percent/workplace-giving/common/hoc/withPermissions'
import { usePostHogFeatureFlag } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'

export function VolunteeringSearchPage() {
  const { t } = useTranslation()
  const { track } = useAnalytics()
  const has = usePermission()
  const volunteeringEnabled = has(scope('volunteering'))

  const [filtersOpen, setFiltersOpen] = useState(false)

  const cityCampaignFiltersEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.WPG_CITY_CAMPAIGN_FILTERS)

  const [query, setQuery] = useQueryParams({
    type: StringParam,
    locationType: StringParam,
    city: ArrayParam,
    campaignId: StringParam,
    country: StringParam,
    relevantSkills: ArrayParam,
    query: StringParam,
    timeOfDay: ArrayParam,
    activities: ArrayParam
  })

  const { query: searchedQuery, ...queryParams } = query
  const { country, city, campaignId, ...baseParams } = queryParams

  const searchParams = cityCampaignFiltersEnabled ? { ...baseParams, city, campaignId } : baseParams
  const countedFilters = cityCampaignFiltersEnabled ? queryParams : { country, ...baseParams }

  const countryCode = useQueryCountryCodeValidation({ code: typeof country !== 'string' ? undefined : country })
  const prevCountryCodeRef = useRef<string>(countryCode)

  // The modal's country options are the allowed list plus, when the applied country is not a member
  // of that list (e.g. a deep-linked out-of-list code), the applied country itself — so the modal's
  // Select can always display the country the page is actually filtering by instead of falling back
  // to Global.
  const modalCountryCodes = useMemo(
    () =>
      countryCode && !volunteeringAllowedOrgCountriesAlpha3.includes(countryCode)
        ? [countryCode, ...volunteeringAllowedOrgCountriesAlpha3]
        : volunteeringAllowedOrgCountriesAlpha3,
    [countryCode]
  )

  const alpha3CountryCodes = useCountryOptions({
    countryCodes: modalCountryCodes,
    includeWorldOption: true
  })

  const activeFiltersAmount = Object.values(countedFilters)?.filter(v => v !== undefined && !!v?.length)?.length

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage, isError } = useInfiniteQuery(
    [
      'searchOpportunities',
      {
        ...searchParams,
        query: searchedQuery,
        targetCountries: countryCode,
        pageSize: 10
      }
    ],
    searchOpportunities,
    {
      staleTime: 0,
      enabled: volunteeringEnabled
    }
  )
  const totalResults = data && data.pages.length > 0 ? data.pages[0].totalResults : 0
  const { ref } = useInfiniteScroll(fetchNextPage)

  const mappedData = mapInfiniteData(data)

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [query])

  const onCountryChange = (newCountry: string) => {
    setQuery({ ...query, country: newCountry })
  }

  const onQueryChange = (q: string) => {
    setQuery({ ...query, query: q })
  }

  const switchToGlobal = () => {
    if (countryCode) {
      prevCountryCodeRef.current = countryCode
    }
    setQuery({ ...query, country: '' })
  }

  const switchToCountry = () => {
    const prevCountryCode = prevCountryCodeRef.current

    if (!prevCountryCode) return
    setQuery({ ...query, country: prevCountryCode })
    prevCountryCodeRef.current = ''
  }

  useEffect(() => {
    if (searchedQuery?.trim().length) {
      track({
        event: 'Search Volunteering',
        properties: {
          origin: 'Volunteering Search Page',
          countryCode
        }
      })
    }
  }, [searchedQuery, countryCode, track])

  const getNoResultsMessage = useMemo(() => {
    if (activeFiltersAmount) {
      return (
        <IllustratedMessage
          illustration="no-results"
          title={t('workplace_giving.volunteering.filters.noResults.title')}
          description={t('workplace_giving.volunteering.filters.noResults.description')}
          cta={{
            label: t('workplace_giving.volunteering.filters.noResults.actionBtn'),
            onClick: () => {
              setQuery({
                relevantSkills: undefined,
                type: undefined,
                locationType: undefined,
                country: '',
                timeOfDay: undefined,
                city: undefined,
                campaignId: undefined
              })
            }
          }}
          ctaShape="oval"
        />
      )
    }

    if (searchedQuery) {
      return (
        <IllustratedMessage
          illustration="no-results"
          title={t('workplace_giving.volunteering.search.noResults.title', {
            searchedPhrase: searchedQuery
          })}
          description={t('workplace_giving.volunteering.search.noResults.description')}
        />
      )
    }

    return (
      <IllustratedMessage
        illustration="nothing-to-see"
        title={t('workplace_giving.volunteering.noResults.title')}
        description={t('workplace_giving.volunteering.noResults.description')}
      />
    )
  }, [activeFiltersAmount, searchedQuery, setQuery, t])

  return (
    <Page testId="volunteering-search-page" fullWidthContent>
      <SearchPageHeader
        query={searchedQuery || ''}
        countryCode={countryCode}
        onCountryChange={onCountryChange}
        onQueryChange={onQueryChange}
        emptyQueryPageTitle={t('workplace_giving.search.exploreVolunteering')}
        breadcrumbsPageName={t('workplace_giving.search.breadcrumbs.volunteering')}
        searchBarPlaceholder={t('workplace_giving.search.placeholder.volunteering')}
        allowEmptyQuery
        hideAiPrompt
      />
      <Flex justify="space-between" align="center" wrap="wrap" gap="xs" mb="md">
        <FiltersButton
          activeFiltersAmount={activeFiltersAmount}
          onClick={() => setFiltersOpen(true)}
          testId="volunteeringFiltersBtn"
          isLoading={isFetching}
        />

        <SwitchToGlobalResults
          totalResults={totalResults}
          currentCountryCode={countryCode}
          prevCountryCode={prevCountryCodeRef.current}
          switchToGlobal={switchToGlobal}
          switchToCountry={switchToCountry}
          isLoading={isFetching}
        />
      </Flex>
      {isLoading ? (
        <div className={classes.ResultsWrapper}>
          <OpportunityCardSkeleton />
          <OpportunityCardSkeleton />
          <OpportunityCardSkeleton />
          <OpportunityCardSkeleton />
          <OpportunityCardSkeleton />
        </div>
      ) : null}
      {!isLoading && !isError ? (
        <div className={classes.ResultsWrapper}>
          {mappedData?.pages?.map(page => page.map(o => <OpportunityCard key={o.id} opportunity={o} />))}
          {hasNextPage && (
            <div ref={ref}>
              <OpportunityCardSkeleton />
            </div>
          )}
        </div>
      ) : null}

      <div className={classes.InfosWrapper}>
        {totalResults === 0 && !isFetching && !isError ? getNoResultsMessage : null}
        {isError && (
          <IllustratedMessage
            illustration="general-error"
            title={t('workplace_giving.errors.somethingWentWrong')}
            description={t('workplace_giving.errors.tryReloadingThePage')}
          />
        )}
      </div>

      <VolunteeringFiltersModal
        query={query}
        setQuery={setQuery}
        open={filtersOpen}
        handleClose={() => setFiltersOpen(false)}
        clearAllActive={!!activeFiltersAmount}
        countries={alpha3CountryCodes}
        countryCode={countryCode}
        onClear={() => {
          if (countryCode) {
            prevCountryCodeRef.current = countryCode
          }
        }}
      />
    </Page>
  )
}
